import { type KeyboardEvent, type ReactNode } from 'react';

import HighlightCard from '@superfunnel/ui-core/HighlightCard';
import RadioIndicator from '@superfunnel/ui-core/RadioIndicator';
import { classNames } from '@superfunnel/ui-core/utils';

interface GalleryCardFrameProps {
  title: string;
  children: ReactNode;
  /** Omit `onToggle` for a read-only card: no selection dot, no button role. */
  isSelected?: boolean;
  onToggle?: () => void;
  radioClassName?: string;
}

export const GalleryCardFrame = ({
  title,
  children,
  isSelected,
  onToggle,
  radioClassName = 'top-3',
}: GalleryCardFrameProps) => (
  <HighlightCard
    selected={isSelected}
    elevate={!!onToggle}
    {...(onToggle
      ? {
          'role': 'button',
          'tabIndex': 0,
          'aria-pressed': isSelected,
          'onClick': onToggle,
          'onKeyDown': (event: KeyboardEvent) => {
            if (event.target !== event.currentTarget) return;
            if (event.key !== 'Enter' && event.key !== ' ') return;
            event.preventDefault();
            onToggle();
          },
        }
      : {})}
    className={classNames(
      'group/card relative flex w-full flex-col gap-1 rounded-2lg p-1 text-start focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500',
      onToggle && 'cursor-pointer',
    )}
  >
    {children}

    {onToggle && (
      <button
        type="button"
        aria-label={`${isSelected ? 'Deselect' : 'Select'} ${title}`}
        onClick={(event) => {
          event.stopPropagation();
          onToggle();
        }}
        className={classNames('absolute right-3 rounded-full', radioClassName)}
      >
        <RadioIndicator checked={!!isSelected} />
      </button>
    )}
  </HighlightCard>
);
